/*
 * Copyright (c) 2018 Barclays PLC.
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Barclays PLC.
 * ("Confidential Information").
 *
 * File: streetAddressFieldSet.js
 * Created: Nov 08, 2018
 */

import React from "react";
import { Field } from "redux-form";
import { required, validateAddress, validateCity, maxLength, minLength } from "../../../utils/validate";
import { toNumber } from "../../../utils/formatters";
import OptionList from "../../Forms/OptionList/optionList";
import Input from "../../Forms/Input/input";
import Select from "../../Forms/Select/select";
import PropTypes from "prop-types";
import hasPoBox from "../../../utils/pobox";
import { memoize } from 'ramda';

const memoizeMaxLength = memoize((max) => maxLength.bind(null, Number(max)));
const validateZip = minLength.bind(null, 5);

const usStates = [
  {
    "value": "AL",
    "text": "AL"
  },
  {
    "value": "AK",
    "text": "AK"
  },
  {
    "value": "AZ",
    "text": "AZ"
  },
  {
    "value": "AR",
    "text": "AR"
  },
  {
    "value": "CA",
    "text": "CA"
  },
  {
    "value": "CO",
    "text": "CO"
  },
  {
    "value": "CT",
    "text": "CT"
  },
  {
    "value": "DE",
    "text": "DE"
  },
  {
    "value": "DC",
    "text": "DC"
  },
  {
    "value": "FL",
    "text": "FL"
  },
  {
    "value": "GA",
    "text": "GA"
  },
  {
    "value": "HI",
    "text": "HI"
  },
  {
    "value": "ID",
    "text": "ID"
  },
  {
    "value": "IL",
    "text": "IL"
  },
  {
    "value": "IN",
    "text": "IN"
  },
  {
    "value": "IA",
    "text": "IA"
  },
  {
    "value": "KS",
    "text": "KS"
  },
  {
    "value": "KY",
    "text": "KY"
  },
  {
    "value": "LA",
    "text": "LA"
  },
  {
    "value": "ME",
    "text": "ME"
  },
  {
    "value": "MD",
    "text": "MD"
  },
  {
    "value": "MA",
    "text": "MA"
  },
  {
    "value": "MI",
    "text": "MI"
  },
  {
    "value": "MN",
    "text": "MN"
  },
  {
    "value": "MS",
    "text": "MS"
  },
  {
    "value": "MO",
    "text": "MO"
  },
  {
    "value": "MT",
    "text": "MT"
  },
  {
    "value": "NE",
    "text": "NE"
  },
  {
    "value": "NV",
    "text": "NV"
  },
  {
    "value": "NH",
    "text": "NH"
  },
  {
    "value": "NJ",
    "text": "NJ"
  },
  {
    "value": "NM",
    "text": "NM"
  },
  {
    "value": "NY",
    "text": "NY"
  },
  {
    "value": "NC",
    "text": "NC"
  },
  {
    "value": "ND",
    "text": "ND"
  },
  {
    "value": "OH",
    "text": "OH"
  },
  {
    "value": "OK",
    "text": "OK"
  },
  {
    "value": "OR",
    "text": "OR"
  },
  {
    "value": "PA",
    "text": "PA"
  },
  {
    "value": "RI",
    "text": "RI"
  },
  {
    "value": "SC",
    "text": "SC"
  },
  {
    "value": "SD",
    "text": "SD"
  },
  {
    "value": "TN",
    "text": "TN"
  },
  {
    "value": "TX",
    "text": "TX"
  },
  {
    "value": "UT",
    "text": "UT"
  },
  {
    "value": "VT",
    "text": "VT"
  },
  {
    "value": "VA",
    "text": "VA"
  },
  {
    "value": "WA",
    "text": "WA"
  },
  {
    "value": "WV",
    "text": "WV"
  },
  {
    "value": "WI",
    "text": "WI"
  },
  {
    "value": "WY",
    "text": "WY"
  }
];

const StreetAddressFieldSet = (props) => {
  const { mailingAddress } = props;

  return hasPoBox(mailingAddress) &&
    (<fieldset className="container">
      <legend className="row">Street Address</legend>
      <div className="row">
        <p className="col-12">Due to compliance with the Patriot Act, we need a street address on file. All
          correspondence will be sent to the Mailing address.</p>
        <div className="col-12 col-lg-6">
          <Field
            name="patriotActStreetAddress1"
            label="Street address"
            component={Input}
            type="text"
            validate={[required, validateAddress, memoizeMaxLength("32")]}
            required={true}
            htmlAttributes={{ maxLength: "32" }}
          />
        </div>
        <div className="col-12 col-lg-6">
          <Field
            name="patriotActStreetAddress2"
            label="Street address 2 (opt)"
            component={Input}
            type="text"
            validate={[validateAddress, memoizeMaxLength("32")]}
            htmlAttributes={{ maxLength: "32" }}
          />
        </div>
        <div className="col-12 col-lg-6">
          <Field
            name="patriotActCity"
            label="City"
            component={Input}
            type="text"
            validate={[required, validateCity, memoizeMaxLength("19")]}
            required={true}
            htmlAttributes={{
              maxLength: "19"
            }}
          />
        </div>
        <div className="col-12 col-lg-3">
          <Field
            name="patriotActState"
            label="State"
            component={Select}
            type="select"
            defaultValue=""
            validate={[required]}
            required={true}
          >
            <OptionList options={usStates} />
          </Field>
        </div>
        <div className="col-12 col-lg-3">
          <Field
            name="patriotActZipcode"
            label="Zip code"
            component={Input}
            type="text"
            normalize={toNumber}
            validate={[required, validateZip]}
            required={true}
            htmlAttributes={{
              minLength: "5",
              maxLength: "5"
            }}
          />
        </div>
      </div>
    </fieldset>);
};

StreetAddressFieldSet.propTypes = {
  mailingAddress: PropTypes.string.isRequired
};

export default StreetAddressFieldSet;

